import { decodeFunctionData, formatUnits, keccak256, toHex, type Address, type Hex } from 'viem';
import {
  bondDepositoryAbi,
  buybackExecutorAbi,
  minterRegistryAbi,
  referralManagerAbi,
  stakingManagerAbi,
  syntraTimelockAbi,
  treasuryVaultAbi,
} from './generated/abis.ts';

/** The addresses a governance call can target; a subset of the deployment record. */
export interface GovernanceTargets {
  timelock: Address;
  staking: Address;
  bonds: Address;
  referral: Address;
  buyback: Address;
  registry: Address;
  treasury: Address;
  oracle?: Address;
  lpLock?: Address;
}

export type ContractName = 'staking' | 'bonds' | 'referral' | 'buyback' | 'registry' | 'timelock' | 'treasury' | 'oracle' | 'lpLock' | 'unknown';
export type RoleName = 'proposer' | 'executor' | 'canceller' | 'pauser' | 'admin' | 'treasurer' | 'keeper' | 'staking' | 'stakeFor' | 'other';

/** A timelocked call, decoded into what it changes, so pages and alerts can say it in words. */
export type AdminCall =
  | { kind: 'rates'; contract: 'staking'; aprs: number[] } // % APR per plan (no lock · 30 · 90 · 180 · 365 d · permanent)
  | { kind: 'fees'; contract: 'staking'; stakeBps: number[]; unstakeBps: number[]; recipient: Address }
  | { kind: 'maxRate'; contract: 'staking'; ratePerEpoch: number } // a fraction
  | { kind: 'referralWiring'; contract: 'staking'; referral: Address }
  | { kind: 'market'; contract: 'bonds'; create: boolean; marketId: number | null; enabled: boolean; roiMinBps: number; roiMaxBps: number; capacity: bigint }
  | { kind: 'buyback'; contract: 'buyback'; triggerBps: number; slippageBps: number; cooldown: number; maxPerTrade: bigint; maxPerDay: bigint; burnBought: boolean }
  | { kind: 'minter'; contract: 'registry'; minter: ContractName; minterAddress: Address; capPerEpoch: bigint; burstEpochs: number; enabled: boolean }
  | { kind: 'globalCap'; contract: 'registry'; capPerEpoch: bigint; burstEpochs: number }
  | { kind: 'referralRates'; contract: 'referral'; directBps: number; minSideBps: number; ranks: { thresholdUsd: bigint; rateBps: number }[] }
  | { kind: 'maxDepth'; contract: 'referral'; depth: number }
  | { kind: 'capMultiplier'; contract: 'referral'; bps: number }
  | { kind: 'pause'; contract: ContractName }
  | { kind: 'unpause'; contract: ContractName }
  | { kind: 'upgrade'; contract: ContractName; implementation: Address }
  | { kind: 'role'; contract: ContractName; grant: boolean; role: RoleName; account: Address }
  | { kind: 'delay'; contract: 'timelock'; seconds: number }
  | { kind: 'treasury'; contract: 'treasury'; fn: string; args: string }
  | { kind: 'unknown'; contract: ContractName; selector: Hex; fn?: string };

const ROLES: Record<string, RoleName> = {
  [`0x${'0'.repeat(64)}`]: 'admin',
  [keccak256(toHex('PROPOSER_ROLE'))]: 'proposer',
  [keccak256(toHex('EXECUTOR_ROLE'))]: 'executor',
  [keccak256(toHex('CANCELLER_ROLE'))]: 'canceller',
  [keccak256(toHex('PAUSER_ROLE'))]: 'pauser',
  [keccak256(toHex('TREASURER_ROLE'))]: 'treasurer',
  [keccak256(toHex('KEEPER_ROLE'))]: 'keeper',
  [keccak256(toHex('STAKING_ROLE'))]: 'staking',
  [keccak256(toHex('STAKE_FOR_ROLE'))]: 'stakeFor',
};

export function roleName(hash: Hex): RoleName {
  return ROLES[hash.toLowerCase()] ?? 'other';
}

/** Which protocol contract an address is, by the deployment record. */
export function contractName(address: Address, d: GovernanceTargets): ContractName {
  const a = address.toLowerCase();
  const names: [ContractName, Address | undefined][] = [
    ['staking', d.staking],
    ['bonds', d.bonds],
    ['referral', d.referral],
    ['buyback', d.buyback],
    ['registry', d.registry],
    ['timelock', d.timelock],
    ['treasury', d.treasury],
    ['oracle', d.oracle],
    ['lpLock', d.lpLock],
  ];
  return names.find(([, x]) => x && x.toLowerCase() === a)?.[0] ?? 'unknown';
}

const ABIS = {
  staking: stakingManagerAbi,
  bonds: bondDepositoryAbi,
  referral: referralManagerAbi,
  buyback: buybackExecutorAbi,
  registry: minterRegistryAbi,
  timelock: syntraTimelockAbi,
  treasury: treasuryVaultAbi,
} as const;

const EPOCHS_PER_YEAR = 730;

/** Decode one timelocked call (target + calldata) into what it changes. Never throws: an unknown call is reported as such. */
export function decodeAdminCall(target: Address, data: Hex, d: GovernanceTargets): AdminCall {
  const contract = contractName(target, d);
  const selector = data.slice(0, 10) as Hex;
  const abi = contract in ABIS ? ABIS[contract as keyof typeof ABIS] : undefined;
  let decoded: { functionName: string; args: readonly unknown[] } | null = null;
  for (const candidate of abi ? [abi, ...Object.values(ABIS)] : Object.values(ABIS)) {
    try {
      const r = decodeFunctionData({ abi: candidate, data });
      decoded = { functionName: r.functionName, args: (r.args ?? []) as readonly unknown[] };
      break;
    } catch {
      /* not in this ABI */
    }
  }
  if (!decoded) return { kind: 'unknown', contract, selector };
  const { functionName: fn, args } = decoded;
  const num = (v: unknown) => Number(v);
  const big = (v: unknown) => BigInt(v as bigint);
  const addr = (v: unknown) => v as Address;
  switch (fn) {
    case 'setRates':
      if (contract === 'staking') return { kind: 'rates', contract, aprs: (args[0] as readonly bigint[]).map((r) => (Number(r) / 1e18) * EPOCHS_PER_YEAR * 100) };
      break;
    case 'setFees':
      if (contract === 'staking') {
        return { kind: 'fees', contract, stakeBps: (args[0] as readonly bigint[]).map(num), unstakeBps: (args[1] as readonly bigint[]).map(num), recipient: addr(args[2]) };
      }
      break;
    case 'setMaxRate':
      if (contract === 'staking') return { kind: 'maxRate', contract, ratePerEpoch: Number(args[0]) / 1e18 };
      break;
    case 'setReferral':
      if (contract === 'staking') return { kind: 'referralWiring', contract, referral: addr(args[0]) };
      break;
    case 'updateMarket':
    case 'createMarket': {
      if (contract !== 'bonds') break;
      const create = fn === 'createMarket';
      const cfg = (create ? args[0] : args[1]) as { enabled: boolean; roiMinBps: number; roiMaxBps: number; capacityPerPeriod: bigint };
      return {
        kind: 'market',
        contract,
        create,
        marketId: create ? null : num(args[0]),
        enabled: cfg.enabled,
        roiMinBps: num(cfg.roiMinBps),
        roiMaxBps: num(cfg.roiMaxBps),
        capacity: big(cfg.capacityPerPeriod),
      };
    }
    case 'setConfig': {
      if (contract !== 'buyback') break;
      const c = args[0] as { triggerBps: number; slippageBps: number; cooldown: number; maxPerTrade: bigint; maxPerDay: bigint; burnBought: boolean };
      return { kind: 'buyback', contract, triggerBps: num(c.triggerBps), slippageBps: num(c.slippageBps), cooldown: num(c.cooldown), maxPerTrade: big(c.maxPerTrade), maxPerDay: big(c.maxPerDay), burnBought: c.burnBought };
    }
    case 'setMinter':
      if (contract === 'registry') {
        return { kind: 'minter', contract, minter: contractName(addr(args[0]), d), minterAddress: addr(args[0]), capPerEpoch: big(args[1]), burstEpochs: num(args[2]), enabled: Boolean(args[3]) };
      }
      break;
    case 'setGlobalCap':
      if (contract === 'registry') return { kind: 'globalCap', contract, capPerEpoch: big(args[0]), burstEpochs: num(args[1]) };
      break;
    case 'setRewardRates':
      if (contract === 'referral') {
        return {
          kind: 'referralRates',
          contract,
          directBps: num(args[0]),
          ranks: (args[1] as readonly { thresholdUsd: bigint; rateBps: number }[]).map((r) => ({ thresholdUsd: big(r.thresholdUsd), rateBps: num(r.rateBps) })),
          minSideBps: num(args[2]),
        };
      }
      break;
    case 'setMaxDepth':
      if (contract === 'referral') return { kind: 'maxDepth', contract, depth: num(args[0]) };
      break;
    case 'setCapMultiplier':
      if (contract === 'referral') return { kind: 'capMultiplier', contract, bps: num(args[0]) };
      break;
    case 'pause':
      return { kind: 'pause', contract };
    case 'unpause':
      return { kind: 'unpause', contract };
    case 'upgradeToAndCall':
      return { kind: 'upgrade', contract, implementation: addr(args[0]) };
    case 'grantRole':
    case 'revokeRole':
      return { kind: 'role', contract, grant: fn === 'grantRole', role: roleName(args[0] as Hex), account: addr(args[1]) };
    case 'updateDelay':
      if (contract === 'timelock') return { kind: 'delay', contract, seconds: num(args[0]) };
      break;
    case 'withdraw':
    case 'withdrawNative':
    case 'approveSpender':
      if (contract === 'treasury') return { kind: 'treasury', contract, fn, args: args.map(String).join(', ') };
      break;
  }
  return { kind: 'unknown', contract, selector, fn };
}

const pct = (bps: number) => `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
const syn = (v: bigint) => Number(formatUnits(v, 18)).toLocaleString('en-US', { maximumFractionDigits: 4 });
const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const duration = (s: number) => (s % 86_400 === 0 ? `${s / 86_400} d` : s % 3600 === 0 ? `${s / 3600} h` : `${Math.round(s / 60)} min`);

export const CONTRACT_LABELS: Record<ContractName, string> = {
  staking: 'Staking',
  bonds: 'Bonds',
  referral: 'Referral',
  buyback: 'Buyback',
  registry: 'Mint registry',
  timelock: 'Timelock',
  treasury: 'Treasury',
  oracle: 'Price oracle',
  lpLock: 'Liquidity lock',
  unknown: 'a contract',
};

export const ROLE_LABELS: Record<RoleName, string> = {
  proposer: 'admin (proposer)',
  executor: 'admin (executor)',
  canceller: 'veto (canceller)',
  pauser: 'pauser',
  admin: 'owner',
  treasurer: 'treasurer',
  keeper: 'keeper',
  staking: 'staking',
  stakeFor: 'stake-for',
  other: 'an unknown',
};

/** The call in one English sentence (the keeper's alerts; the app translates the same structure). */
export function describeAdminCall(c: AdminCall): string {
  const C = CONTRACT_LABELS[c.contract];
  switch (c.kind) {
    case 'rates':
      return `Staking rates change to ${c.aprs.map((a) => `${Math.round(a * 100) / 100}%`).join(' · ')} APR (no lock · 30 · 90 · 180 · 365 d · permanent)`;
    case 'fees':
      return `Staking fees change: in ${c.stakeBps.map(pct).join(' · ')}, out ${c.unstakeBps.map(pct).join(' · ')}; fee wallet ${short(c.recipient)}`;
    case 'maxRate':
      return `The maximum staking rate changes to ${(c.ratePerEpoch * 100).toFixed(3)}% per epoch`;
    case 'referralWiring':
      return `Staking is wired to the referral contract ${short(c.referral)}`;
    case 'market':
      return `${c.create ? 'A new bond market' : `Bond market ${c.marketId}`}: ${c.enabled ? 'open' : 'closed'}, ROI ${pct(c.roiMinBps)}–${pct(c.roiMaxBps)}, capacity ${syn(c.capacity)} SYN per period`;
    case 'buyback':
      return `Buyback settings: trigger ${pct(c.triggerBps)} below the 7-day high, up to ${syn(c.maxPerTrade)} USDT per trade and ${syn(c.maxPerDay)} per day, slippage ${pct(c.slippageBps)}, cooldown ${duration(c.cooldown)}, bought SYN ${c.burnBought ? 'burned' : 'kept in the treasury'}`;
    case 'minter':
      return `Mint budget of ${CONTRACT_LABELS[c.minter]}: ${syn(c.capPerEpoch)} SYN per epoch, burst ${c.burstEpochs} epochs, ${c.enabled ? 'enabled' : 'disabled'}`;
    case 'globalCap':
      return `Global mint budget: ${syn(c.capPerEpoch)} SYN per epoch, burst ${c.burstEpochs} epochs`;
    case 'referralRates':
      return `Referral rates: direct ${pct(c.directBps)}; ranks ${c.ranks.map((r) => `${pct(r.rateBps)} from $${syn(r.thresholdUsd)}`).join(', ')}; at least ${pct(c.minSideBps)} per side`;
    case 'maxDepth':
      return `Referral volume climbs ${c.depth} levels`;
    case 'capMultiplier':
      return `Referral cap: ${pct(c.bps)} of the peak active stake`;
    case 'pause':
      return `${C} is paused`;
    case 'unpause':
      return `${C} is unpaused`;
    case 'upgrade':
      return `The ${C} contract is upgraded to the implementation ${short(c.implementation)}`;
    case 'role':
      return `${short(c.account)} ${c.grant ? 'gets' : 'loses'} the ${ROLE_LABELS[c.role]} role on ${C}`;
    case 'delay':
      return `The timelock delay changes to ${duration(c.seconds)}`;
    case 'treasury':
      return `Treasury ${c.fn}: ${c.args}`;
    case 'unknown':
      return `Call ${c.fn ?? c.selector} on ${C}`;
  }
}
